import type { Image } from 'types';

// Rules shared by the media library's selection, bulk actions and ZIP export.
// Kept free of React and browser APIs so they can be unit tested in Node.

export type MediaItem = Partial<Image> & {
  /** Older video rows expose the file as `url` instead of `image_url`. */
  url?: string;
  /** Free-plan artificial queue: the tile hides the result until this time. */
  _queuedUntil?: number;
};

export type MediaKind = 'image' | 'video';

/** The placeholder clip the video webhooks write for a terminal failure. */
export const FAILED_VIDEO_URL = 'https://videos.easy-peasy.ai/202507251214.mp4';

export const getMediaKind = (item: MediaItem): MediaKind =>
  item.is_video ? 'video' : 'image';

const getVideoUrl = (item: MediaItem) => item.url || item.image_url || '';

export const isMediaFailed = (item: MediaItem): boolean => {
  if (item.is_video) return getVideoUrl(item) === FAILED_VIDEO_URL;
  return !item.image_url && Boolean(item.request?.failed);
};

/** Still generating: no file yet and no failure recorded. */
export const isMediaPending = (item: MediaItem): boolean => {
  if (isMediaFailed(item)) return false;
  return item.is_video ? !getVideoUrl(item) : !item.image_url;
};

export const isMediaQueued = (item: MediaItem, now = Date.now()): boolean =>
  typeof item._queuedUntil === 'number' && now < item._queuedUntil;

/**
 * Whether a tile takes part in selection. Pending generations have nothing to
 * act on yet, queued Free-plan results are still hidden from their owner, and
 * gallery examples are not the user's media.
 */
export const isMediaSelectable = (item: MediaItem, now = Date.now()): boolean =>
  typeof item.id === 'number' &&
  !item.isFromExample &&
  !isMediaPending(item) &&
  !isMediaQueued(item, now);

/** The file a download should fetch, or null when there is nothing to save. */
export const getMediaDownloadUrl = (item: MediaItem): string | null => {
  if (isMediaFailed(item) || isMediaPending(item)) return null;
  if (item.is_video) return getVideoUrl(item) || null;
  return item.four_k_image_url || item.image_url || null;
};

/** Only the owner can trash, restore or favorite a row (the API enforces it too). */
export const isMediaOwnedBy = (item: MediaItem, userId?: string | null) =>
  Boolean(userId) && item.user_id === userId;

const KNOWN_EXTENSIONS = new Set([
  'png',
  'jpg',
  'jpeg',
  'webp',
  'gif',
  'avif',
  'heic',
  'mp4',
  'mov',
  'webm',
  'm4v',
]);

export const extensionFromUrl = (url: string): string | null => {
  try {
    const { pathname } = new URL(url);
    const last = pathname.split('/').pop() || '';
    const dot = last.lastIndexOf('.');
    if (dot < 0) return null;
    const ext = last.slice(dot + 1).toLowerCase();
    if (!KNOWN_EXTENSIONS.has(ext)) return null;
    return ext === 'jpeg' ? 'jpg' : ext;
  } catch {
    return null;
  }
};

const CONTENT_TYPE_EXTENSIONS: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/jpg': 'jpg',
  'image/webp': 'webp',
  'image/gif': 'gif',
  'image/avif': 'avif',
  'image/heic': 'heic',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'video/webm': 'webm',
};

export const extensionFromContentType = (
  contentType?: string | null,
): string | null => {
  if (!contentType) return null;
  const type = contentType.split(';')[0].trim().toLowerCase();
  return CONTENT_TYPE_EXTENSIONS[type] || null;
};

export const resolveMediaExtension = (
  item: MediaItem,
  url: string,
  contentType?: string | null,
): string =>
  extensionFromUrl(url) ||
  extensionFromContentType(contentType) ||
  (item.is_video ? 'mp4' : 'png');

const MAX_SLUG_LENGTH = 48;

/** A readable, filesystem-safe fragment of the prompt (any script, no punctuation). */
export const slugifyForFileName = (value: string): string => {
  const slug = value
    .normalize('NFKC')
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');
  const chars = Array.from(slug).slice(0, MAX_SLUG_LENGTH);
  return chars.join('').replace(/-+$/g, '');
};

/** The creation day in the viewer's time zone (prompts.csv keeps the exact UTC time). */
const datePart = (createdAt?: string) => {
  if (!createdAt) return '';
  const date = new Date(createdAt);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/**
 * `2026-10-05_cat-on-a-windowsill_5412345.png` — date first so a folder sorts
 * chronologically, the id last so names never collide and stay traceable.
 */
export const buildMediaFileName = (
  item: MediaItem,
  extension: string,
  used?: Set<string>,
  /** e.g. `-upscaled`, appended before the extension. */
  suffix = '',
): string => {
  const label = slugifyForFileName(
    item.user_prompt || item.prompt || item.title || item.model || '',
  );
  const parts = [
    datePart(item.created_at),
    label || getMediaKind(item),
    item.id !== undefined ? String(item.id) : '',
  ].filter(Boolean);
  const base = `${parts.join('_')}${suffix}`;
  let name = `${base}.${extension}`;
  if (used) {
    let copy = 2;
    while (used.has(name)) name = `${base}-${copy++}.${extension}`;
    used.add(name);
  }
  return name;
};

const csvCell = (value: unknown) => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export interface MediaManifestRow {
  file: string;
  item: MediaItem;
}

/** prompts.csv: what each file in the archive was made from. BOM so Excel reads UTF-8. */
export const buildPromptsCsv = (rows: MediaManifestRow[]): string => {
  const header = ['file', 'id', 'type', 'created_at', 'model', 'prompt'];
  const lines = rows.map(({ file, item }) =>
    [
      file,
      item.id,
      getMediaKind(item),
      item.created_at || '',
      item.model || '',
      item.user_prompt || item.prompt || '',
    ]
      .map(csvCell)
      .join(','),
  );
  return `﻿${[header.join(','), ...lines].join('\r\n')}\r\n`;
};

/**
 * Ids between the anchor and the target (both included) in display order.
 * Falls back to just the target when the anchor is no longer on screen.
 */
export const getRangeIds = (
  orderedIds: number[],
  anchorId: number | null,
  targetId: number,
): number[] => {
  const to = orderedIds.indexOf(targetId);
  if (to < 0) return [];
  const from = anchorId === null ? -1 : orderedIds.indexOf(anchorId);
  if (from < 0) return [targetId];
  const [start, end] = from <= to ? [from, to] : [to, from];
  return orderedIds.slice(start, end + 1);
};

// Rough per-file sizes for deciding, before anything is fetched, whether an
// export is big enough to stream to disk instead of building it in memory.
const ESTIMATED_IMAGE_BYTES = 2.5 * 1024 * 1024;
const ESTIMATED_VIDEO_BYTES = 12 * 1024 * 1024;

const estimateItemBytes = (item: MediaItem) =>
  item.is_video ? ESTIMATED_VIDEO_BYTES : ESTIMATED_IMAGE_BYTES;

export const estimateDownloadBytes = (items: MediaItem[]): number =>
  items.reduce((total, item) => total + estimateItemBytes(item), 0);

// One download is one ZIP the browser builds while the tab stays open, so it is
// kept to a few minutes and well under 4 GB (no ZIP64, which some unzip tools
// mishandle). Bigger selections go in batches: what doesn't fit stays
// selected for the next click.
export const DOWNLOAD_BATCH_MAX_ITEMS = 500;
export const DOWNLOAD_BATCH_MAX_ESTIMATED_BYTES = 2 * 1024 * 1024 * 1024;

/** Splits items (in order) into this download and the rest. */
export const planDownloadBatch = (
  items: MediaItem[],
  maxItems = DOWNLOAD_BATCH_MAX_ITEMS,
  maxEstimatedBytes = DOWNLOAD_BATCH_MAX_ESTIMATED_BYTES,
): { batch: MediaItem[]; rest: MediaItem[] } => {
  let bytes = 0;
  let count = 0;
  for (const item of items) {
    const next = bytes + estimateItemBytes(item);
    // Always take at least one item, even a huge one.
    if (count > 0 && (count >= maxItems || next > maxEstimatedBytes)) break;
    bytes = next;
    count += 1;
  }
  return { batch: items.slice(0, count), rest: items.slice(count) };
};

export const formatBytes = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit++;
  }
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
};
